import type { UserAvatarUser } from '#layers/access/types/user-avatar';
import type { FetchFunction, ResponseEnvelope } from '#layers/base/public';
import type { Filter } from '#layers/data-model/public';
import { isObjectEmpty, stringifyQuery } from '#layers/base/public';

/**
 * Members are users (`MonospaceSystemUser`) scoped to the current workspace by the
 * `GET /{workspace}/members` endpoint. `UserAvatarUser` is exactly what an avatar row needs, which
 * is all the picker renders.
 */
const MEMBER_FIELDS: ReadonlyArray<keyof UserAvatarUser> = ['id', 'fullName', 'email', 'avatarId'];

interface SearchWorkspaceMembersOptions {
	fetch: FetchFunction;
	/** Case-insensitive search over `fullName` and `email`; empty returns the first `limit` members. */
	search: string;
	/** Filter AND-combined with the search, e.g. to omit members already connected to the relation. */
	filter?: Filter;
	limit: number;
	offset: number;
	signal?: AbortSignal;
}

export interface WorkspaceMemberSearchPage {
	members: UserAvatarUser[];
	totalCount: number;
}

function buildSearchFilter(search: string, filter: Filter | undefined): Filter | undefined {
	const filters: Filter[] = [];

	const trimmed = search.trim();

	if (trimmed) {
		filters.push({
			_or: [
				{ fullName: { _icontains: trimmed } },
				{ email: { _icontains: trimmed } },
			],
		});
	}

	// Callers may pass an empty object when their filter cannot be built yet; it would match nothing
	// useful and only bloat the query.
	if (filter && !isObjectEmpty(filter)) {
		filters.push(filter);
	}

	if (filters.length === 0) return undefined;
	if (filters.length === 1) return filters[0];
	return { _and: filters };
}

/**
 * Server-side, workspace-member-scoped search over the users collection. The endpoint AND-combines
 * its member-scope pre-filter with the supplied `filter`, so only current members are ever returned.
 */
export async function searchWorkspaceMembers(options: SearchWorkspaceMembersOptions): Promise<WorkspaceMemberSearchPage> {
	const { fetch, search, filter, limit, offset, signal } = options;

	const query = stringifyQuery({
		fields: MEMBER_FIELDS,
		filter: buildSearchFilter(search, filter),
		limit,
		offset,
		meta: { totalCount: true },
	});

	const response = await fetch<ResponseEnvelope<UserAvatarUser[]>>('members', { query, signal });
	const members = response.data ?? [];

	return { members, totalCount: response.meta?.totalCount ?? offset + members.length };
}
